import { useMemo, useState, type ReactNode } from "react";
import { ActionIcon, CopyButton, Group, Text, Tooltip } from "@mantine/core";
import { IconBinaryTree, IconBraces, IconCheck, IconCopy, IconDownload, IconFoldDown, IconFoldUp } from "@tabler/icons-react";
import { codeColors } from "../theme/tokens";
import { highlight, type CodeLanguage } from "./highlight";

const C = codeColors;

function Primitive({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span style={{ color: C.null }}>{String(value)}</span>;
  if (typeof value === "string") {
    const shown = value.startsWith("data:") && value.length > 140 ? `${value.slice(0, 80)}…(${value.length} chars)` : value;
    return <span style={{ color: C.string }}>{JSON.stringify(shown)}</span>;
  }
  if (typeof value === "number") return <span style={{ color: C.number }}>{value}</span>;
  if (typeof value === "boolean") return <span style={{ color: C.boolean }}>{String(value)}</span>;
  return <span>{String(value)}</span>;
}

function Node({ name, value, depth, expandDepth, last }: { name?: string; value: unknown; depth: number; expandDepth: number; last: boolean }) {
  const isObj = value !== null && typeof value === "object";
  const [open, setOpen] = useState(depth < expandDepth);
  const comma = last ? null : <span style={{ color: C.punctuation }}>,</span>;
  const label: ReactNode =
    name !== undefined ? (
      <>
        <span style={{ color: C.key }}>{JSON.stringify(name)}</span>
        <span style={{ color: C.punctuation }}>: </span>
      </>
    ) : null;

  if (!isObj) {
    return (
      <div style={{ paddingLeft: depth ? 16 : 0 }}>
        <span className="pd-json-toggle" />
        {label}
        <Primitive value={value} />
        {comma}
      </div>
    );
  }
  const entries = Array.isArray(value) ? value.map((v, i) => [String(i), v] as const) : Object.entries(value as Record<string, unknown>);
  const [o, c] = Array.isArray(value) ? ["[", "]"] : ["{", "}"];
  if (!entries.length) {
    return (
      <div style={{ paddingLeft: depth ? 16 : 0 }}>
        <span className="pd-json-toggle" />
        {label}
        <span style={{ color: C.punctuation }}>
          {o}
          {c}
        </span>
        {comma}
      </div>
    );
  }
  return (
    <div style={{ paddingLeft: depth ? 16 : 0 }}>
      <span className="pd-json-toggle" onClick={() => setOpen(!open)} role="button" aria-label={open ? "Collapse" : "Expand"}>
        {open ? "▾" : "▸"}
      </span>
      {label}
      <span style={{ color: C.punctuation }}>{o}</span>
      {open ? (
        <>
          {entries.map(([k, v], i) => (
            <Node key={k} name={Array.isArray(value) ? undefined : k} value={v} depth={depth + 1} expandDepth={expandDepth} last={i === entries.length - 1} />
          ))}
          <span className="pd-json-toggle" />
          <span style={{ color: C.punctuation }}>{c}</span>
        </>
      ) : (
        <span style={{ color: C.null, cursor: "pointer" }} onClick={() => setOpen(true)}>
          {" "}
          {Array.isArray(value) ? `${entries.length} items` : `${entries.length} keys`} <span style={{ color: C.punctuation }}>{c}</span>
        </span>
      )}
      {comma}
    </div>
  );
}

const fmtBytes = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(2)} MB`);

function download(text: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: fileName });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ToolButton({ label, onClick, children, active }: { label: string; onClick: () => void; children: ReactNode; active?: boolean }) {
  return (
    <Tooltip label={label}>
      <ActionIcon size="sm" variant={active ? "light" : "subtle"} color={active ? "lime" : "gray"} onClick={onClick} aria-label={label}>
        {children}
      </ActionIcon>
    </Tooltip>
  );
}

function CopyTool({ value, label }: { value: string; label: string }) {
  return (
    <CopyButton value={value}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? "Copied" : label}>
          <ActionIcon size="sm" variant="subtle" color={copied ? "lime" : "gray"} onClick={copy} aria-label={label}>
            {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}

/**
 * JSON on the dark code surface: a collapsible tree by default, or the raw document (pretty-printed
 * and colour-coded) for reading or copying everything. Long values wrap instead of scrolling sideways.
 */
export function JsonView({
  value,
  expandDepth = 2,
  maxHeight = 520,
  fileName = "response.json",
  defaultMode = "tree",
}: {
  value: unknown;
  expandDepth?: number;
  maxHeight?: number | string;
  fileName?: string;
  defaultMode?: "tree" | "raw";
}) {
  const [key, setKey] = useState(0);
  const [depth, setDepth] = useState(expandDepth);
  const [mode, setMode] = useState(defaultMode);
  const text = useMemo(() => JSON.stringify(value, null, 2) ?? "", [value]);
  const size = useMemo(() => fmtBytes(new Blob([text]).size), [text]);
  const raw = useMemo(() => (mode === "raw" ? highlight(text, "json") : null), [mode, text]);
  return (
    <div className="pd-code-frame">
      <Group className="pd-code-bar" justify="space-between" wrap="nowrap">
        <Group gap={2} wrap="nowrap">
          <ToolButton label="Tree view" onClick={() => setMode("tree")} active={mode === "tree"}>
            <IconBinaryTree size={14} />
          </ToolButton>
          <ToolButton label="Raw JSON (formatted)" onClick={() => setMode("raw")} active={mode === "raw"}>
            <IconBraces size={14} />
          </ToolButton>
          <Text fz={11} c="var(--pd-ink-muted)" ff="monospace" ml={6}>
            {mode === "raw" ? "raw JSON" : "tree"} · {size}
          </Text>
        </Group>
        <Group gap={2} wrap="nowrap">
          {mode === "tree" && (
            <>
              <ToolButton label="Expand all" onClick={() => (setDepth(99), setKey(key + 1))}>
                <IconFoldDown size={14} />
              </ToolButton>
              <ToolButton label="Collapse" onClick={() => (setDepth(1), setKey(key + 1))}>
                <IconFoldUp size={14} />
              </ToolButton>
            </>
          )}
          <ToolButton label={`Download ${fileName}`} onClick={() => download(text, fileName)}>
            <IconDownload size={14} />
          </ToolButton>
          <CopyTool value={text} label="Copy JSON" />
        </Group>
      </Group>
      {mode === "raw" ? (
        <pre className="pd-code" style={{ maxHeight, margin: 0 }}>
          {raw}
        </pre>
      ) : (
        <div className="pd-code" style={{ maxHeight }} key={key}>
          <Node value={value} depth={0} expandDepth={depth} last />
        </div>
      )}
    </div>
  );
}

export function CodeBlock({ code, maxHeight = 420, language }: { code: string; maxHeight?: number; language?: CodeLanguage }) {
  const body = useMemo(() => (language ? highlight(code, language) : code), [code, language]);
  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "absolute", top: 6, right: 8, zIndex: 1 }}>
        <CopyTool value={code} label="Copy code" />
      </div>
      <pre className="pd-code" style={{ maxHeight, margin: 0, paddingRight: 40 }}>
        {body}
      </pre>
    </div>
  );
}
